import React, { useEffect, useState } from 'react';

const API = process.env.REACT_APP_API_URL;

const SERVICES = ['claims', 'enrollment', 'utilization', 'hra', 'pharmacy', 'network', 'fdr_oversight', 'other'];

export default function FdrRegistry() {
  const [fdrs, setFdrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  function load() {
    setLoading(true);
    fetch(`${API}/api/universe-requests/fdrs?include_inactive=true`, { credentials: 'include' })
      .then(r => r.json()).then(d => setFdrs(d.fdrs || []))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  async function save(form) {
    const res = await fetch(`${API}/api/universe-requests/fdrs`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    if (res.ok) { setEditing(null); load(); }
  }

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-slate-600">
          Persistent registry of every FDR/TPA/delegated entity for this organization. Used by every Universe Request.
        </div>
        <button onClick={() => setEditing({})} className="bg-red-700 hover:bg-red-800 text-white px-3 py-1.5 text-sm font-semibold">
          Add FDR
        </button>
      </div>

      {fdrs.length === 0 && !editing && (
        <div className="bg-white border border-slate-200 p-8 text-center">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">No FDRs yet</div>
          <div className="text-sm text-slate-700">Add your first FDR to start requesting universes.</div>
        </div>
      )}

      {fdrs.length > 0 && (
        <table className="w-full text-sm bg-white border border-slate-200">
          <thead className="text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
            <tr>
              <th className="text-left py-2 px-3">Name</th>
              <th className="text-left py-2">Email</th>
              <th className="text-left py-2">TPA Platform</th>
              <th className="text-left py-2">Services</th>
              <th className="text-left py-2">BAA</th>
              <th className="text-right py-2 pr-3">Active</th>
            </tr>
          </thead>
          <tbody>
            {fdrs.map(f => (
              <tr key={f.id} onClick={() => setEditing(f)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                <td className="py-2 px-3 font-semibold text-slate-900">{f.fdr_name}</td>
                <td className="py-2 font-mono text-xs text-slate-700">{f.contact_email}</td>
                <td className="py-2 text-slate-700">{f.tpa_platform || '—'}</td>
                <td className="py-2 text-xs text-slate-600">{(f.services_provided || []).join(', ') || '—'}</td>
                <td className="py-2">
                  <span className={`text-[10px] font-mono uppercase tracking-wider border px-1.5 py-0.5 ${
                    f.baa_on_file ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                  }`}>{f.baa_on_file ? 'on file' : 'missing'}</span>
                </td>
                <td className="py-2 pr-3 text-right text-xs font-mono">{f.active ? '✓' : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <FdrEditor fdr={editing} onSave={save} onCancel={() => setEditing(null)}/>
        </div>
      )}
    </div>
  );
}

function FdrEditor({ fdr, onSave, onCancel }) {
  const [form, setForm] = useState({
    id: fdr.id, fdr_name: fdr.fdr_name || '', contact_email: fdr.contact_email || '',
    contact_name: fdr.contact_name || '', contact_phone: fdr.contact_phone || '',
    tpa_platform: fdr.tpa_platform || '',
    services_provided: fdr.services_provided || [],
    primary_mfa_channel: fdr.primary_mfa_channel || 'email',
    baa_on_file: fdr.baa_on_file || false,
    baa_signed_at: fdr.baa_signed_at || '',
    active: fdr.active === undefined ? true : !!fdr.active,
    notes: fdr.notes || '',
  });

  function toggleService(s) {
    setForm(f => ({
      ...f,
      services_provided: f.services_provided.includes(s)
        ? f.services_provided.filter(x => x !== s)
        : [...f.services_provided, s],
    }));
  }

  return (
    <div className="bg-white w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
      <h3 className="text-sm font-semibold text-slate-900 mb-4">{form.id ? 'Edit FDR' : 'Add FDR'}</h3>
      <div className="space-y-3">
        <Field label="FDR Name *" value={form.fdr_name} onChange={v => setForm({...form, fdr_name: v})}/>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact Email *" value={form.contact_email} onChange={v => setForm({...form, contact_email: v})}/>
          <Field label="Contact Name" value={form.contact_name} onChange={v => setForm({...form, contact_name: v})}/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact Phone" value={form.contact_phone} onChange={v => setForm({...form, contact_phone: v})}/>
          <Field label="TPA Platform" value={form.tpa_platform} onChange={v => setForm({...form, tpa_platform: v})}/>
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Services provided</label>
          <div className="flex flex-wrap gap-2">
            {SERVICES.map(s => (
              <button key={s} onClick={() => toggleService(s)}
                className={`text-xs font-mono px-2 py-1 border ${
                  form.services_provided.includes(s)
                    ? 'border-red-700 text-red-700 bg-red-50'
                    : 'border-slate-300 text-slate-600 hover:border-slate-500'
                }`}>{s}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">MFA channel</label>
            <select value={form.primary_mfa_channel} onChange={e => setForm({...form, primary_mfa_channel: e.target.value})} className="w-full border border-slate-300 px-2 py-1 text-sm">
              <option value="email">email</option>
              <option value="sms">sms</option>
            </select>
          </div>
          <label className="text-xs font-mono uppercase tracking-widest text-slate-500 flex items-center gap-2 pt-5">
            <input type="checkbox" checked={form.baa_on_file} onChange={e => setForm({...form, baa_on_file: e.target.checked})}/>
            BAA on file
          </label>
          <label className="text-xs font-mono uppercase tracking-widest text-slate-500 flex items-center gap-2 pt-5">
            <input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})}/>
            Active
          </label>
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Notes</label>
          <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={3}
            className="w-full border border-slate-300 px-2 py-1 text-sm"/>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button onClick={onCancel} className="text-sm text-slate-600 hover:text-slate-900 px-4 py-2">Cancel</button>
        <button onClick={() => onSave(form)} className="bg-red-700 hover:bg-red-800 text-white px-6 py-2 text-sm font-semibold">Save</button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">{label}</label>
      <input value={value} onChange={e => onChange(e.target.value)} className="w-full border border-slate-300 px-2 py-1 text-sm"/>
    </div>
  );
}
